import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { InspectResult, KIND_OPTIONS, UploadKind, guessSentence } from '../utils/uploadKind';

// "What is this file?" — shown after every CSV is inspected and before anything
// is written. Register sales, money in and bills out never mix, and direction
// cannot always be read from a file, so the owner confirms (2026-09-24).
export default function UploadKindDialog({
  fileName, inspect, busy, onConfirm, onCancel,
}: {
  fileName: string;
  inspect: InspectResult;
  busy: boolean;
  onConfirm: (kind: UploadKind) => void;
  onCancel: () => void;
}) {
  const [kind, setKind] = useState<UploadKind>(inspect.guess);
  useEffect(() => setKind(inspect.guess), [inspect.guess]);
  const mismatch = kind === 'sales' && inspect.guess !== 'sales';

  return (
    <div className="fixed inset-0 bg-black/30 backdrop-blur-[2px] flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.97, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        className="bg-white rounded-[18px] shadow-xl border border-[#E6E2DE] w-full max-w-md"
        role="dialog"
        aria-labelledby="upload-kind-title"
      >
        <div className="p-6 border-b border-[#E6E2DE]">
          <h3 id="upload-kind-title" className="font-serif text-lg text-[#2D2A2E]">What is this file?</h3>
          <p className="font-sans text-sm text-[#6B6670] mt-1">{guessSentence(inspect.rows, fileName, inspect.guess)}</p>
          {inspect.reasons[0] && (
            <p className="font-sans text-xs text-[#6B6670] mt-1">We think so because {inspect.reasons[0]}.</p>
          )}
        </div>
        <div className="p-6 space-y-2">
          {KIND_OPTIONS.map((o) => (
            <label
              key={o.kind}
              className={`flex items-start gap-3 rounded-[10px] border px-4 py-3 cursor-pointer transition-colors ${
                kind === o.kind ? 'border-[#4A2D5E] bg-[#4A2D5E]/5' : 'border-[#E6E2DE] hover:border-[#4A2D5E]/40'
              }`}
            >
              <input
                type="radio"
                name="upload-kind"
                className="mt-1 accent-[#4A2D5E]"
                checked={kind === o.kind}
                onChange={() => setKind(o.kind)}
              />
              <span>
                <span className="block font-sans text-sm font-medium text-[#2D2A2E]">{o.label}</span>
                <span className="block font-sans text-xs text-[#6B6670]">{o.hint}</span>
              </span>
            </label>
          ))}
          {mismatch && (
            <p className="font-sans text-xs text-[#B54A4A] pt-1">
              This doesn&rsquo;t look like register sales — the sales pages may not make sense of it.
            </p>
          )}
        </div>
        <div className="px-6 pb-6 flex gap-3">
          <button
            onClick={onCancel}
            disabled={busy}
            className="flex-1 bg-white border border-[#E6E2DE] text-[#2D2A2E] hover:bg-[#FAF9F7] rounded-[8px] h-10 font-sans font-medium text-sm transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(kind)}
            disabled={busy}
            className="flex-1 bg-[#4A2D5E] text-white hover:bg-[#3D254D] rounded-[8px] h-10 font-sans font-medium text-sm transition-colors disabled:opacity-50"
          >
            {busy ? 'Importing…' : 'Import'}
          </button>
        </div>
      </motion.div>
    </div>
  );
}
